import { useContext, useEffect, useState, useRef } from 'react';
import { setMsal } from 'src/api/API';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { EventMessage, EventType, InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
// DataLagoon
import { DataLagoonContext } from './DataLagoonContext';
import { protectedResources } from './authConfig';

export default function Me() {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { state, update } = useContext(DataLagoonContext);
  const [loggedIn, setLoggedIn] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const [popupInProgress, setPopupInProgress] = useState(false);
  const fetchInProgress = useRef(false);
  const lastFetchTime = useRef(0);

  useEffect(() => {
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_SUCCESS) {
        setLoggedIn(true);
      }
    });

    if (isAuthenticated) {
      setLoggedIn(true);
    }

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const method = 'GET';
    const apiUrl = `${protectedResources.dataLagoonApi.endpoint}/me`;
    const now = Date.now();
    
    // Debounce to prevent multiple simultaneous fetches (wait at least 2 seconds between calls)
    if (fetchInProgress.current || (now - lastFetchTime.current) < 2000) {
      return;
    }

    if (
      (account && state.me === null && loggedIn === true) ||
      (account && state.me === -1 && isAuthenticated === true)
    ) {
      fetchInProgress.current = true;
      lastFetchTime.current = now;
      update({ loading: true });
      instance
        .acquireTokenSilent({
          scopes: protectedResources.dataLagoonApi.scopes,
          account,
          forceRefresh: false, // Use cached token if available
        })
        .then(async (response) => {
          try {
            const meResponse = await fetch(apiUrl, {
              method,
              headers: {
                Authorization: `Bearer ${response.accessToken}`,
                'Content-Type': 'application/json',
              },
            });

            if (meResponse.status === 200) {
              const json = await meResponse.json();
              update({ me: json, loading: false });
              fetchInProgress.current = false;
            } else if (meResponse.status === 401) {
              console.warn('Received 401 from /me endpoint - token expired, logging out');
              update({ me: null, loading: false });
              fetchInProgress.current = false;
              lastFetchTime.current = 0;
              instance.logoutRedirect({
                postLogoutRedirectUri: window.location.origin,
              });
            } else if (meResponse.status === 403) {
              update({ loading: false });
              fetchInProgress.current = false;
            } else {
              update({ loading: false });
              fetchInProgress.current = false;
            }
          } catch (fetchError) {
            update({ loading: false });
            fetchInProgress.current = false;
            console.error('Network or parsing error during /me call:', fetchError);
            if (fetchError instanceof InteractionRequiredAuthError) {
              throw fetchError;
            }
          }
        })
        .catch((error) => {
          console.error('Token acquisition error:', error);
          update({ loading: false });
          // Reset debounce so the retry path isn't blocked by the 2s guard
          lastFetchTime.current = 0;
          
          // Handle different error types
          const isInteractionRequired = 
            error instanceof InteractionRequiredAuthError ||
            error?.errorCode === 'interaction_required' ||
            error?.errorCode === 'consent_required' ||
            error?.errorCode === 'login_required' ||
            error?.errorCode === 'token_renewal_error';
          
          // Check if user cancelled or popup is blocked
          const isUserCancelled = 
            error instanceof BrowserAuthError &&
            (error.errorCode === 'user_cancelled' || error.errorCode === 'popup_window_error');

          // Only show popup if not already in progress, not cancelled, and interaction is required
          if (
            isInteractionRequired &&
            account &&
            !popupInProgress &&
            !isUserCancelled &&
            (state.me === null || state.me === -1)
          ) {
            setPopupInProgress(true);
            update({ loading: true });
            
            // Add a small delay to prevent rapid popup attempts
            setTimeout(() => {
              instance
                .acquireTokenPopup({
                  scopes: protectedResources.dataLagoonApi.scopes,
                  prompt: 'select_account', // Allow user to select account
                })
                .then(async (response) => {
                  try {
                    const meResponse = await fetch(apiUrl, {
                      method,
                      headers: {
                        Authorization: `Bearer ${response.accessToken}`,
                        'Content-Type': 'application/json',
                      },
                    });

                    if (meResponse.status === 200) {
                      const json = await meResponse.json();
                      update({ me: json, loading: false });
                    } else if (meResponse.status === 401) {
                      // Even after popup, still getting 401 - logout
                      console.error('Still receiving 401 after popup - logging out');
                      update({ loading: false });
                      instance.logoutRedirect({
                        postLogoutRedirectUri: window.location.origin,
                      });
                    } else if (meResponse.status === 403) {
                      update({ loading: false });
                    } else {
                      update({ loading: false });
                    }
                  } catch (fetchError) {
                    update({ loading: false });
                    console.error('Error fetching user data after popup:', fetchError);
                  } finally {
                    setPopupInProgress(false);
                    fetchInProgress.current = false;
                  }
                })
                .catch((popupError) => {
                  update({ loading: false });
                  setPopupInProgress(false);
                  fetchInProgress.current = false;
                  console.error('Popup authentication failed:', popupError);
                  
                  // If popup fails, set me to -1 to prevent infinite loop
                  if (popupError?.errorCode === 'popup_window_error') {
                    update({ me: -1 });
                  }
                });
            }, 500);
          } else {
            fetchInProgress.current = false;
            // If user cancelled, don't retry
            if (isUserCancelled) {
              update({ me: -1 });
            }
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, loggedIn, state.me, isAuthenticated]);

  useEffect(() => {
    if (instance && account) {
      setMsal(instance, account);
    }
  }, [instance, account]);
  return null;
}
